import { useState, useEffect } from "react";
import { Lock, Award, TrendingUp } from "lucide-react";
import { getAllBadges, getUserBadges, getUserStats } from "../lib/gamification";
import type { BadgeDefinition, UserBadge, UserStats } from "../lib/supabase";

export function Badges() {
  const [allBadges, setAllBadges] = useState<BadgeDefinition[]>([]);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [badges, earned, userStats] = await Promise.all([
        getAllBadges(),
        getUserBadges(),
        getUserStats(),
      ]);

      console.log("🏆 All badges loaded:", badges);
      console.log("✅ User earned badges:", earned);
      console.log("📊 User stats loaded:", userStats);

      setAllBadges(badges);
      setUserBadges(earned);
      setStats(userStats);
    } finally {
      setLoading(false);
    }
  };

  const isEarned = (badgeId: string): boolean => {
    return userBadges.some((ub) => ub.badge_id === badgeId);
  };

  const getEarnedDate = (badgeId: string): string | null => {
    const badge = userBadges.find((ub) => ub.badge_id === badgeId);
    return badge ? new Date(badge.earned_at).toLocaleDateString() : null;
  };

  const getProgress = (badge: BadgeDefinition): string | null => {
    console.log(`📊 Current stats for badge progress:`, stats);
    console.log(`🏅 Checking progress for badge:`, badge.id);

    if (!stats) {
      console.log("❌ No stats available");
      return null;
    }

    switch (badge.id) {
      case "perfect_week":
        console.log(
          `📅 Perfect Week progress: ${stats.current_streak_days}/7 days`
        );
        if (stats.current_streak_days >= 7) return null; // Don't show progress if completed
        return `${stats.current_streak_days}/7 days`;
      case "consistency_champion":
        console.log(
          `🏆 Consistency Champion progress: ${stats.current_streak_days}/30 days`
        );
        if (stats.current_streak_days >= 30) return null; // Don't show progress if completed
        return `${stats.current_streak_days}/30 days`;
      case "iron_will":
        console.log(
          `💪 Iron Will progress: ${stats.current_streak_days}/90 days`
        );
        if (stats.current_streak_days >= 90) return null; // Don't show progress if completed
        return `${stats.current_streak_days}/90 days`;
      case "health_warrior":
        console.log(
          `⚔️ Health Warrior progress: ${stats.current_streak_days}/365 days`
        );
        if (stats.current_streak_days >= 365) return null; // Don't show progress if completed
        return `${stats.current_streak_days}/365 days`;
      case "century_club":
        console.log(
          `💯 Century Club progress: ${stats.total_doses_taken}/100 doses`
        );
        if (stats.total_doses_taken >= 100) return null; // Don't show progress if completed
        return `${stats.total_doses_taken}/100 doses`;
      case "knowledge_seeker":
        console.log(
          `📚 Knowledge Seeker progress: ${stats.facts_viewed_count}/50 facts`
        );
        if (stats.facts_viewed_count >= 50) return null; // Don't show progress if completed
        return `${stats.facts_viewed_count}/50 facts`;
      default:
        console.log(
          `❓ Unknown badge ID: ${badge.id} - no progress tracking implemented`
        );
        return null;
    }
  };

  const categories = ["all", "streak", "milestone", "behavior", "achievement"];
  const filteredBadges =
    selectedCategory === "all"
      ? allBadges
      : allBadges.filter((b) => b.category === selectedCategory);

  const earnedCount = userBadges.length;
  const totalCount = allBadges.length;
  const completionPercent =
    totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-500">Loading badges...</div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 safe-area-x">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Award className="text-warm-primary" size={32} />
          <div>
            <h1 className="text-2xl font-bold text-warm-dark">
              Badge Collection
            </h1>
            <p className="text-gray-700 text-sm">
              {earnedCount} of {totalCount} badges unlocked
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Overall Progress
            </span>
            <span className="text-sm font-bold text-orange-600">
              {completionPercent}%
            </span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? "bg-orange-500 text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredBadges.map((badge, index) => {
          const earned = isEarned(badge.id);
          const earnedDate = getEarnedDate(badge.id);
          const progress = getProgress(badge);

          console.log(`🎯 Badge "${badge.name}" (${badge.id}):`, {
            earned,
            progress,
            stats: stats
              ? {
                  current_streak_days: stats.current_streak_days,
                  total_doses_taken: stats.total_doses_taken,
                  facts_viewed_count: stats.facts_viewed_count,
                }
              : "No stats",
          });

          return (
            <div
              key={badge.id}
              className={`rounded-xl border-2 p-6 transition-all duration-300 hover:scale-[1.02] animate-slide-in ${
                earned
                  ? "bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200 shadow-md"
                  : "bg-gray-50 border-gray-200"
              }`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`text-5xl ${
                    earned ? "animate-shimmer" : "grayscale opacity-40"
                  }`}
                >
                  {badge.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3
                      className={`font-bold text-lg ${
                        earned ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {badge.name}
                    </h3>
                    {!earned && (
                      <Lock className="text-gray-400 flex-shrink-0" size={18} />
                    )}
                  </div>

                  <p
                    className={`text-sm mb-3 ${
                      earned ? "text-gray-700" : "text-gray-500"
                    }`}
                  >
                    {badge.description}
                  </p>

                  {earned && earnedDate && (
                    <div className="text-xs text-green-600 font-medium bg-green-50 inline-block px-2 py-1 rounded">
                      Unlocked {earnedDate}
                    </div>
                  )}

                  {progress && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <TrendingUp size={14} />
                        <span className="font-medium">{progress}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-400 to-orange-500 transition-all duration-500"
                          style={{
                            width: `${Math.min(
                              (parseInt(progress.split("/")[0]) /
                                parseInt(progress.split("/")[1])) *
                                100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredBadges.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No badges in this category yet.
        </div>
      )}
    </div>
  );
}
